import { dec2,num } from "@/5-shared/utils/format";

/**
 * §4 Copertura naturale, modello e geometria di `HedgeStory`: la linea della spesa,
 * le barre dei ricavi DEC e la linea del costo netto su dieci anni.
 *
 * Tutte le serie sono in € totali annui, su un solo asse y con lo zero alla base.
 */

const YEARS = 10;
export const MWH = 5000; // consumo annuo illustrativo
const PBASE = 120; // €/MWh di riferimento

/**
 * Serie di prezzo illustrativa, scritta a mano: parte bassa, sale, esplode all'anno 4,
 * rientra. Un array letterale invece di una somma di seni perché il picco va messo
 * dove serve al racconto, e resta deterministico a module load.
 */
export const PRICE: number[] = [88, 104, 141, 224, 178, 116, 92, 128, 146, 112];

type Tech = "eolico" | "solare";

/**
 * `revBase` = ricavi per MWh consumato al prezzo di riferimento; `beta` = quanta parte
 * di ogni variazione del prezzo si ritrova nei ricavi. L'eolico produce anche nelle ore
 * care e cattura meglio il prezzo; il solare produce a mezzogiorno, quando il prezzo è
 * già schiacciato dal solare di tutti gli altri.
 */
export const TECH: Record<Tech, { label: string; revBase: number; beta: number }> = {
  eolico: { label: "Eolico", revBase: 66, beta: 0.7 },
  solare: { label: "Solare", revBase: 58, beta: 0.45 },
};

export const cost = (p: number) => p * MWH;
export const revenue = (p: number, revBase: number, beta: number) => MWH * (revBase + beta * (p - PBASE));
export const net = (p: number, revBase: number, beta: number) => cost(p) - revenue(p, revBase, beta);

export const costSeries = () => PRICE.map(cost);
export const netSeries = (revBase: number, beta: number) => PRICE.map((p) => net(p, revBase, beta));

const mean = (vals: number[]) => vals.reduce((a, b) => a + b, 0) / vals.length;
export const sigma = (vals: number[]) => {
  const m = mean(vals);
  return Math.sqrt(vals.reduce((a, b) => a + (b - m) ** 2, 0) / vals.length);
};

export const W = 640;
export const H = 340;
export const PAD_L = 64; // etichette dell'asse y: "€ 1.25 M" è largo ~53, finisce a PAD_L − 8
export const PAD_R = 16;
const PAD_T = 30;
const PAD_B = 42; // etichette anno sotto la baseline
/** tetto dell'asse: la spesa massima (€ 1,12 M) con aria sopra */
const VMAX = 1_250_000;

const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;
export const BASELINE = H - PAD_B;

export const BAR_W = 30;
/* le colonne sono centrate sull'anno: rientrare di mezza barra ai due estremi, altrimenti
   la prima e l'ultima sbordano sull'asse y e sul bordo destro */
export const x = (i: number) => PAD_L + BAR_W / 2 + (i / (YEARS - 1)) * (PLOT_W - BAR_W);
export const y = (v: number) => BASELINE - (v / VMAX) * PLOT_H;
export const linePath = (vals: number[]) => vals.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");

export const barX = (i: number) => x(i) - BAR_W / 2;

/** forma corta per assi e numeroni: `€ 1,12 M` / `€ 440 k` */
export const compact = (v: number) => {
  const a = Math.abs(v);
  if (a >= 1_000_000) return `€ ${dec2.format(v / 1_000_000)} M`;
  return `€ ${num.format(Math.round(v / 1000))} k`;
};
